// src/lib/utils/base64/base64.ts

export const encodeBase64 = (str: string): string => {
    // Environnement React Native
    if (typeof global !== "undefined" && global.btoa) {
        // Gestion des caractères Unicode pour React Native
        return global.btoa(
            encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
                String.fromCharCode(parseInt(p1, 16)),
            ),
        );
    }
    // Environnement Node.js
    else if (typeof Buffer !== "undefined") {
        return Buffer.from(str, "utf8").toString("base64");
    }
    // Environnement navigateur
    else if (typeof window !== "undefined" && window.btoa) {
        // Gestion des caractères Unicode pour le navigateur
        return window.btoa(
            encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
                String.fromCharCode(parseInt(p1, 16)),
            ),
        );
    }
    // Fallback avec TextEncoder si disponible (navigateurs modernes)
    else if (typeof TextEncoder !== "undefined") {
        const encoder = new TextEncoder();
        const bytes = encoder.encode(str);
        let binary = "";
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return typeof window !== "undefined"
            ? window.btoa(binary)
            : global.btoa(binary);
    }
    // Fallback (peu probable)
    else {
        throw new Error("No base64 encoding method available");
    }
};

/**
 * Encode une string en Base64URL de manière compatible React + React Native
 * @param message - La string à encoder
 * @returns La string encodée en Base64URL
 */
export function encodeBase64URL(message: string): string {
    try {
        let base64: string;

        // React Native
        if (typeof global !== "undefined" && global.btoa) {
            // Gestion des caractères Unicode pour React Native
            const utf8Message = encodeURIComponent(message).replace(
                /%([0-9A-F]{2})/g,
                (_, p1) => String.fromCharCode(parseInt(p1, 16)),
            );
            base64 = global.btoa(utf8Message);
        }
        // Navigateur (React)
        else if (typeof window !== "undefined" && window.btoa) {
            // Gestion des caractères Unicode pour le navigateur
            const utf8Message = encodeURIComponent(message).replace(
                /%([0-9A-F]{2})/g,
                (_, p1) => String.fromCharCode(parseInt(p1, 16)),
            );
            base64 = window.btoa(utf8Message);
        }
        // Node.js (fallback)
        else if (typeof Buffer !== "undefined") {
            base64 = Buffer.from(message, "utf8").toString("base64");
        }
        // Fallback avec TextEncoder
        else if (typeof TextEncoder !== "undefined") {
            const encoder = new TextEncoder();
            const data = encoder.encode(message);

            let binary = "";
            for (let i = 0; i < data.length; i++) {
                binary += String.fromCharCode(data[i]);
            }

            // Essayer btoa dans l'environnement disponible
            if (typeof window !== "undefined" && window.btoa) {
                base64 = window.btoa(binary);
            } else if (typeof global !== "undefined" && global.btoa) {
                base64 = global.btoa(binary);
            } else {
                throw new Error("No base64 encoding method available");
            }
        } else {
            throw new Error("No base64 encoding method available");
        }

        // Conversion Base64 vers Base64URL
        return base64
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=+$/, "");
    } catch (error) {
        console.error(`Error encoding base64url: ${error}`);
        return "";
    }
}

/**
 * Décode une string Base64URL de manière compatible React + React Native
 * @param data - La string Base64URL à décoder
 * @returns La string décodée
 */
export function decodeBase64URL(data: string): string {
    try {
        // Conversion base64url vers base64
        const base64 = data.replace(/-/g, "+").replace(/_/g, "/");

        // Ajout du padding si nécessaire
        const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);

        let decodedString: string;

        // React Native
        if (typeof global !== "undefined" && global.atob) {
            const binaryString = global.atob(padded);
            // Décodage des caractères Unicode
            decodedString = decodeURIComponent(
                binaryString
                    .split("")
                    .map(
                        (c) =>
                            "%" +
                            ("00" + c.charCodeAt(0).toString(16)).slice(-2),
                    )
                    .join(""),
            );
        }
        // Navigateur (React)
        else if (typeof window !== "undefined" && window.atob) {
            const binaryString = window.atob(padded);
            // Décodage des caractères Unicode
            decodedString = decodeURIComponent(
                binaryString
                    .split("")
                    .map(
                        (c) =>
                            "%" +
                            ("00" + c.charCodeAt(0).toString(16)).slice(-2),
                    )
                    .join(""),
            );
        }
        // Node.js (fallback)
        else if (typeof Buffer !== "undefined") {
            decodedString = Buffer.from(padded, "base64").toString("utf8");
        }
        // Fallback avec TextDecoder
        else if (typeof TextDecoder !== "undefined") {
            // Essayer atob dans l'environnement disponible
            let binaryString: string;
            if (typeof window !== "undefined" && window.atob) {
                binaryString = window.atob(padded);
            } else if (typeof global !== "undefined" && global.atob) {
                binaryString = global.atob(padded);
            } else {
                throw new Error("No base64 decoding method available");
            }

            // Conversion en Uint8Array
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            // Décodage UTF-8
            const decoder = new TextDecoder("utf-8");
            decodedString = decoder.decode(bytes);
        } else {
            throw new Error("No base64 decoding method available");
        }

        return decodedString;
    } catch (error) {
        console.error(`Error decoding base64url: ${error}`);
        return "";
    }
}

/**
 * Convertit base64url vers base64 standard pour données binaires
 * Compatible React et React Native
 * @param base64url - Données base64url de Gmail
 * @returns Base64 standard pour usage web (data URLs)
 */
export function convertBase64URLToBinary(base64url: string): string {
    try {
        // Conversion base64url vers base64 standard
        let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");

        // Ajout du padding si nécessaire
        const padLength = (4 - (base64.length % 4)) % 4;
        base64 += "=".repeat(padLength);

        // Validation du format base64
        if (!/^[A-Za-z0-9+/]*={0,2}$/.test(base64)) {
            throw new Error("Invalid base64url format");
        }

        // Le base64 résultant est déjà utilisable pour les data URLs
        return base64;
    } catch (error) {
        console.error(`Error converting base64url to binary: ${error}`);
        return "";
    }
}
